






















































































import { Component, Ref, Vue } from "vue-property-decorator";
import router from "@/router/router";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import InfoConfigProblemListComponent from "@/components/admin/infoConfig/InfoConfigProblemList.vue";
import BeschreibungSectionsComponent from "@/components/admin/infoConfig/BeschreibungSections.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import { BeschreibungFile, decodeBeschreibung, draftFrom, parseBeschreibung, readDraft, writeDraft } from "@/utils/BeschreibungFile";
import { InfoConfigKind, InfoConfigVersionDetail, InfoConfigVersionRow } from "@/data/models/InfoConfigAdministration";
import {
  getInfoConfigVersion,
  listInfoConfigVersions,
  retireInfoConfigVersion,
  setGlobalInfoConfigTarget,
} from "@/data/repository/InfoConfigAdministrationRepository";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";
import { isNamedPortalSysAdmin } from "@/permissions/UserPermissions";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { DownloadFileType, getFileTypeIcon } from "@/utils/FileTypeIcons";
import {
  InfoConfigBadge,
  infoConfigErrorKey,
  isTarget,
  splitProblems,
  suggestNextVersion,
  versionBadges,
} from "@/utils/InfoConfigAdministration";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";

/**
 * One Beschreibung version, read-only: its problems and its rows by section, each option with the index
 * a project stores for it. A named SysAdmin can make a room version the global target, or retire it (it
 * stays readable for the projects that use it; a target cannot be retired). Addressed by `kind` and
 * `version`, so a reload or a shared link works.
 */
@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
    "problem-list": InfoConfigProblemListComponent,
    "beschreibung-sections": BeschreibungSectionsComponent,
    "content-state": ContentStateComponent,
  },
})
export default class InfoConfigVersionDetailComponent extends Vue {
  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  detail: InfoConfigVersionDetail | null = null;
  /** The file in its sections and columns, from `detail.contentBase64`. */
  file: BeschreibungFile | null = null;
  row: InfoConfigVersionRow | null = null;
  /** The room versions, for the number a new draft suggests. */
  roomRows: InfoConfigVersionRow[] = [];
  search = "";
  isLoading = true;
  notFound = false;
  busy = false;

  get kind(): InfoConfigKind {
    return this.$route.query.kind === "LEAK_DETECTION" ? "LEAK_DETECTION" : "ROOM";
  }

  get version(): string {
    const value = this.$route.query.version;
    return typeof value === "string" ? value : "";
  }

  readonly csvIcon = getFileTypeIcon(DownloadFileType.CSV);

  get isLegacy(): boolean {
    return this.kind === "LEAK_DETECTION";
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get badges(): InfoConfigBadge[] {
    return this.row ? versionBadges(this.row) : [];
  }

  /** Changes are for a named SysAdmin, on room versions only; the legacy files are read-only. */
  get canChange(): boolean {
    return isNamedPortalSysAdmin(getReactiveActiveUser()) && !this.isLegacy && this.row !== null;
  }

  get canBecomeGlobalTarget(): boolean {
    return (
      this.row !== null &&
      !this.row.isGlobalTarget &&
      !this.row.retired &&
      this.detail !== null &&
      splitProblems(this.detail.problems).errors.length === 0
    );
  }

  get canRetire(): boolean {
    return this.row !== null && !this.row.retired && !isTarget(this.row);
  }

  /**
   * A draft of a new version from this one, kept in this browser (one at a time: an open draft is
   * replaced only after asking). The number is one above the newest.
   */
  async onNewDraft() {
    if (!this.detail) {
      return;
    }
    const existing = readDraft(window.localStorage);
    if (existing) {
      const replace = await this.confirmDialog.show({
        title: this.$t("admin.infoConfig.draft.replaceTitle").toString(),
        message: this.$t("admin.infoConfig.draft.replaceMessage", [existing.version, existing.baseVersion]).toString(),
        acceptMessage: this.$t("admin.infoConfig.draft.replace").toString(),
        rejectMessage: this.$t("admin.infoConfig.draft.openExisting").toString(),
      });
      if (!replace) {
        router.push({ name: ComponentName.INFO_CONFIG_DRAFT });
        return;
      }
    }
    if (!writeDraft(window.localStorage, draftFrom(this.version, suggestNextVersion(this.roomRows), this.detail.contentBase64))) {
      SnackbarModule.showError({ key: "admin.infoConfig.draft.notSaved" });
      return;
    }
    router.push({ name: ComponentName.INFO_CONFIG_DRAFT });
  }

  onBack() {
    router.push({ name: ComponentName.INFO_CONFIG_LIST });
  }

  async onSetGlobalTarget() {
    const confirmed = await this.confirmDialog.show({
      title: this.$t("admin.infoConfig.detail.setGlobalTargetTitle").toString(),
      message: this.$t("admin.infoConfig.detail.setGlobalTargetMessage", [this.version]).toString(),
      acceptMessage: this.$t("admin.infoConfig.detail.setGlobalTarget").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (confirmed) {
      await this.run(() => setGlobalInfoConfigTarget(this.version), "admin.infoConfig.detail.globalTargetSet");
    }
  }

  async onRetire() {
    const confirmed = await this.confirmDialog.show({
      title: this.$t("admin.infoConfig.detail.retireTitle").toString(),
      message: this.$t("admin.infoConfig.detail.retireMessage", [this.version]).toString(),
      acceptMessage: this.$t("admin.infoConfig.detail.retire").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (confirmed) {
      await this.run(() => retireInfoConfigVersion(this.version), "admin.infoConfig.detail.retired");
    }
  }

  async run(change: () => Promise<void>, successKey: string) {
    this.busy = true;
    try {
      await change();
      SnackbarModule.showSuccess({ key: successKey, params: [this.version] });
      await this.refreshAfterChange();
    } catch (error) {
      SnackbarModule.showError({ key: infoConfigErrorKey(error) });
    } finally {
      this.busy = false;
    }
  }

  async load() {
    this.isLoading = true;
    this.notFound = false;
    try {
      const [detail, list] = await Promise.all([getInfoConfigVersion(this.kind, this.version), listInfoConfigVersions()]);
      this.detail = detail;
      const { text, bom } = decodeBeschreibung(detail.contentBase64);
      this.file = parseBeschreibung(text, bom);
      this.roomRows = list.room;
      this.row = (this.isLegacy ? list.leakDetection : list.room).find((entry) => entry.version === this.version) ?? null;
    } catch (error) {
      // Swallowed on purpose: the screen shows "not found" or its failure state.
      const code = (error as { code?: unknown } | null)?.code;
      this.notFound = code === "not-found" || code === "functions/not-found";
      this.detail = null;
    } finally {
      this.isLoading = false;
      ToolbarContentModule.setToolbarTitle(`${this.$t("admin.infoConfig.title")} > ${this.version}`);
    }
  }

  /**
   * After making the version the global target or retiring it: reads the version list again in the
   * background, without the page's loading state, so the sections stay where they are and only the badges
   * and buttons update (Taimoor, 2026-10-20: no full reload after a save). The file itself cannot change
   * through either action, so it is not read again; the full {@link load} stays for opening the page.
   */
  async refreshAfterChange() {
    try {
      const list = await listInfoConfigVersions();
      this.roomRows = list.room;
      this.row = (this.isLegacy ? list.leakDetection : list.room).find((entry) => entry.version === this.version) ?? null;
    } catch {
      // Swallowed on purpose: the page keeps what it showed, and the change is on the server.
    }
  }

  mounted() {
    ToolbarContentModule.setI18nResource("admin.infoConfig.title");
    this.load();
  }
}
